import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { Box, Chip, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CustomPagination from '../../../components/customPagination';
import { formatCurrentPeriod } from '../../../utils/date';
import { useGetSubscriptionsHandler } from '../dataHandlers/getSubscriptionsHandler';
import SubscriptionActionMenu from './SubscriptionActionMenu';
import SubscriptionOrganizationCell from './SubscriptionOrganizationCell';

const SUBSCRIPTION_TYPE_LABELS = {
  prepaid: 'Prepaid',
  postpaid: 'Postpaid',
};

const getSubscriptionStatusColor = (status) => {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'success';
    case 'past_due':
    case 'unpaid':
    case 'incomplete':
      return 'warning';
    case 'canceled':
    case 'incomplete_expired':
      return 'error';
    default:
      return 'default';
  }
};

export default function SubscriptionsTable({
  handlePaginationChange,
  searchQuery = {},
  paginationModel = { page: 0, pageSize: 10 },
}) {
  const { subscriptions, totalDocuments, isLoading } = useGetSubscriptionsHandler({ searchQuery });

  const handleCopyId = async (value) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      // Ignore clipboard errors to avoid blocking table interactions.
    }
  };

  const renderCopyableIdCell = (value, to) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', height: '100%' }}>
      {to ? (
        <Link
          component={RouterLink}
          to={to}
          underline="hover"
          variant="body2"
          title={value || '-'}
          sx={{
            minWidth: 0,
            flexGrow: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value || '-'}
        </Link>
      ) : (
        <Typography
          variant="body2"
          title={value || '-'}
          sx={{
            minWidth: 0,
            flexGrow: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value || '-'}
        </Typography>
      )}
      <Tooltip title="Copy ID">
        <span>
          <IconButton
            size="small"
            color="primary"
            sx={{ ml: 0.5, flexShrink: 0 }}
            aria-label={`copy-${value || 'id'}`}
            disabled={!value}
            onClick={(event) => {
              event.stopPropagation();
              handleCopyId(value);
            }}
          >
            <ContentCopy fontSize="inherit" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  const columns = [
    {
      field: 'stripe_subscription_id',
      headerName: 'Stripe Subscription ID',
      flex: 0.5,
      renderCell: (params) => renderCopyableIdCell(params.value, `/subscriptions/${params.value}`),
    },
    {
      field: 'organization',
      headerName: 'Organization',
      flex: 1.25,
      sortable: false,
      renderCell: (params) => <SubscriptionOrganizationCell stripeCustomerId={params.row?.stripe_customer_id} />,
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.35,
      valueFormatter: (value) => SUBSCRIPTION_TYPE_LABELS[value] || value || 'NA',
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.25,
      renderCell: (params) => {
        const status = params.value || 'NA';

        return <Chip label={status} size="small" color={getSubscriptionStatusColor(params.value)} />;
      },
    },
    {
      field: 'current_period',
      headerName: 'Current period',
      flex: 0.75,
      valueGetter: (_value, row) => formatCurrentPeriod(row?.current_period_start, row?.current_period_end),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.25,
      renderCell: (params) => <SubscriptionActionMenu subscription={params.row} />,
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={subscriptions}
        columns={columns}
        getRowId={(row) => row.stripe_subscription_id}
        paginationMode="server"
        rowCount={totalDocuments}
        loading={isLoading}
        paginationModel={paginationModel}
        pageSizeOptions={[10, 25]}
        disableColumnMenu
        disableColumnSorting
        disableColumnFilter
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => {
          handlePaginationChange(model.page, model.pageSize);
        }}
        slots={{ pagination: CustomPagination }}
      />
    </Box>
  );
}
